import { useCallback, useEffect } from 'react';
import { useFlexPropertySelection } from '../../hooks/useFlexPropertySelection';
import { useAlertHelpers } from '../../components/layout/Alert/AlertHelpers';
import { parseCurrency } from '../../utils/formatters';
import { useFormManagement } from './hooks/useFormManagement';
import { usePlanosCalculation } from './hooks/usePlanosCalculation';
import { useSimulationActions } from './hooks/useSimulationActions';
import { useSignalDistribution } from './hooks/useSignalDistribution';
import { getParcelasLimits } from './utils/planoRules';
import { ALL_PLANOS } from '../../utils/constantes';

/**
 * Hook principal que orquestra toda a lógica do FlexSimulator
 * Compõe múltiplos hooks especializados para separação de responsabilidades
 */
export const useFlexSimulatorLogic = (isMRV = true) => {
  // Hooks de gerenciamento de estado
  const { showSuccess, showError } = useAlertHelpers();
  const { formData, setFormData, handlePlanoChange, resetForm, intermediates, handleIntermediatesChange } = useFormManagement();

  // Primeiro criamos propertySelection sem callback
  const propertySelection = useFlexPropertySelection(isMRV);

  // Hooks de cálculos e lógica de negócio
  const { planosDisponiveis, tiposFluxoDisponiveis, tiposCorrecaoDisponiveis } = usePlanosCalculation({
    propertySelection,
    selectedPlano: formData.plano,
    formData,
    setFormData
  });

  const {
    dadosInicializacao,
    valorFinal,
    valorTabelaState,
    loadingInicializacao,
    loadingSimulacao,
    simulationResult,
    showResult,
    isPrintEnabled,
    enviarAprovacao,
    resetSimulationState,
    hideResult
  } = useSimulationActions({
    formData,
    setFormData,
    propertySelection,
    planosDisponiveis,
    showSuccess,
    showError,
    intermediates
  });

  // Calcular limites de parcelas
  const planoSelecionado = formData.plano ? planosDisponiveis[formData.plano] : '';
  const parcelasLimits = getParcelasLimits(planoSelecionado, dadosInicializacao?.quantidadeParcelasPSA, formData.eSAD, dadosInicializacao?.quantidadeMaximaParcelasMensais);

  // Ajustar quantidade de parcelas quando SAD for desmarcado e o valor exceder o limite
  useEffect(() => {
    // Só ajusta se for plano Pós Obra e houver um limite máximo definido
    if (planoSelecionado === ALL_PLANOS.PosObra && parcelasLimits.max) {
      const parcelasAtual = Number.parseInt(formData.quantidadeParcelas) || 0;
      
      // Se a quantidade atual exceder o máximo permitido, ajusta para o máximo
      if (parcelasAtual > parcelasLimits.max) {
        const maxPermitido = dadosInicializacao?.quantidadeMaximaParcelasMensais || parcelasLimits.max;
        const novoValor = Math.min(parcelasLimits.max, maxPermitido);
        
        setFormData(prev => ({
          ...prev,
          quantidadeParcelas: novoValor
        }));
      }
    }
  }, [formData.eSAD, formData.quantidadeParcelas, planoSelecionado, parcelasLimits.max, dadosInicializacao?.quantidadeMaximaParcelasMensais, setFormData]);

  // Calcular valor final efetivo (com desconto de FGTS e Desconto)
  const valorFinalEfetivo = valorFinal - (parseCurrency(formData.fgts) || 0) - (parseCurrency(formData.desconto) || 0);

  // Hook para distribuição automática dos sinais
  const signalDistribution = useSignalDistribution({
    valorFinal: valorFinalEfetivo,
    setFormData,
    plano: planoSelecionado,
    minParcelas: parcelasLimits.min || 0,
    maxParcelas: parcelasLimits.max || dadosInicializacao?.quantidadeMaximaParcelasMensais || 999
  });

  // Wrappers para os handlers de sinais que ocultam o resultado
  const handleAtoChange = useCallback((value) => {
    hideResult();
    signalDistribution.handleAtoChange(value);
  }, [hideResult, signalDistribution]);

  const handleCartaDeCreditoChange = useCallback((value, atoMinimo) => {
    hideResult();
    signalDistribution.handleCartaDeCreditoChange(value, atoMinimo);
  }, [hideResult, signalDistribution]);

  const handleE2Change = useCallback((value, atoValue) => {
    hideResult();
    signalDistribution.handleE2Change(value, atoValue);
  }, [hideResult, signalDistribution]);

  const handleE3Change = useCallback((value, atoValue) => {
    hideResult();
    signalDistribution.handleE3Change(value, atoValue);
  }, [hideResult, signalDistribution]);

  const handleQuantidadeParcelasChange = useCallback((value) => {
    hideResult();
    signalDistribution.handleQuantidadeParcelasChange(value);
  }, [hideResult, signalDistribution]);

  const handleQuantidadeParcelasBlur = useCallback((value) => {
    hideResult();
    value = value.replace(/^0+(?!$)/, '');
    setFormData(prev => ({
      ...prev,
      quantidadeParcelas: value
    }));
    signalDistribution.handleQuantidadeParcelasBlur();
  }, [hideResult, signalDistribution, setFormData]);

  // Wrapper para setFormData que oculta o resultado
  const setFormDataWithHide = useCallback((updater) => {
    hideResult();
    setFormData(updater);
  }, [hideResult, setFormData]);

  // Wrappers para os handlers de propriedade que ocultam o resultado
  const handleEstadoChange = useCallback((value) => {
    hideResult();
    propertySelection.handleEstadoChange(value);
  }, [hideResult, propertySelection]);

  const handleCidadeChange = useCallback((value) => {
    hideResult();
    propertySelection.handleCidadeChange(value);
  }, [hideResult, propertySelection]);

  const handleEmpreendimentoChange = useCallback((value) => {
    hideResult();
    propertySelection.handleEmpreendimentoChange(value);
  }, [hideResult, propertySelection]);

  const handleBlocoChange = useCallback((value) => {
    hideResult();
    propertySelection.handleBlocoChange(value);
  }, [hideResult, propertySelection]);

  const handleUnidadeChange = useCallback((value) => {
    hideResult();
    propertySelection.handleUnidadeChange(value);
    // Limpar os campos do formulário quando a unidade mudar
    if (value) {
      resetForm();
      resetSimulationState();
    }
  }, [hideResult, propertySelection, resetForm, resetSimulationState]);

  const handlePagadoriaChange = useCallback((value) => {
    hideResult();
    propertySelection.handlePagadoriaChange(value);
  }, [hideResult, propertySelection]);

  const handleRendaExteriorChange = useCallback((value) => {
    hideResult();
    propertySelection.handleRendaExteriorChange(value);
  }, [hideResult, propertySelection]);

  const handleClearEmpreendimento = useCallback(() => {
    hideResult();
    propertySelection.handleClearEmpreendimento();
  }, [hideResult, propertySelection]);

  const handleClearUnidade = useCallback(() => {
    hideResult();
    propertySelection.handleUnidadeChange('');
  }, [hideResult, propertySelection]);

  // Wrapper para handlePlanoChange que oculta o resultado
  const updatePlanoWithHide = useCallback((plano) => {
    hideResult();
    handlePlanoChange(plano);
  }, [hideResult, handlePlanoChange]);

  // Wrapper para handleIntermediatesChange que oculta o resultado
  const handleIntermediatesChangeWithHide = useCallback((intermediates) => {
    hideResult();
    handleIntermediatesChange(intermediates);
  }, [hideResult, handleIntermediatesChange]);

  // Handler unificado para FGTS e Desconto que redistribui os sinais
  const handleAdjustmentChange = useCallback((value, fieldName) => {
    hideResult();
    
    // Atualizar o campo (fgts ou desconto)
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Se for plano À Vista ou Fixo, redistribuir os sinais com o novo valor final efetivo
    if ((planoSelecionado === ALL_PLANOS.AVista || planoSelecionado === ALL_PLANOS.Fixo12 || planoSelecionado === ALL_PLANOS.Fixo18) && valorFinal > 0) {
      // Calcular novo valor final efetivo considerando FGTS + Desconto
      const fgtsValue = fieldName === 'fgts' ? value : formData.fgts;
      const descontoValue = fieldName === 'desconto' ? value : formData.desconto;
      const novoValorFinalEfetivo = valorFinal - (parseCurrency(fgtsValue) || 0) - (parseCurrency(descontoValue) || 0);
      
      if (planoSelecionado === ALL_PLANOS.AVista && novoValorFinalEfetivo > 0) {
        // Para À Vista, redistribuir igualmente entre Ato, E2 e E3
        const valorPorSinal = novoValorFinalEfetivo / 3;
        
        setFormData(prev => ({
          ...prev,
          [fieldName]: value,
          ato: (valorPorSinal).toFixed(2).replace('.', ','),
          e2: (valorPorSinal).toFixed(2).replace('.', ','),
          e3: (valorPorSinal).toFixed(2).replace('.', ',')
        }));
      } else if ((planoSelecionado === ALL_PLANOS.Fixo12 || planoSelecionado === ALL_PLANOS.Fixo18) && novoValorFinalEfetivo > 0) {
        // Para Fixo, recalcular com a nova quantidade de parcelas
        const quantidadeParcelas = Number.parseInt(formData.quantidadeParcelas) || 1;
        const valorPorParcela = novoValorFinalEfetivo / quantidadeParcelas;
        
        setFormData(prev => ({
          ...prev,
          [fieldName]: value,
          ato: (valorPorParcela).toFixed(2).replace('.', ','),
          e2: (valorPorParcela).toFixed(2).replace('.', ','),
          e3: (valorPorParcela).toFixed(2).replace('.', ',')
        }));
      }
    }
  }, [formData, hideResult, setFormData, planoSelecionado, valorFinal]);

  // Wrappers para manter a interface original
  const handleFgtsChange = useCallback((value) => {
    handleAdjustmentChange(value, 'fgts');
  }, [handleAdjustmentChange]);

  const handleDescontoChange = useCallback((value) => {
    handleAdjustmentChange(value, 'desconto');
  }, [handleAdjustmentChange]);
 
const handlePrintClick = useCallback((unitName = null) => {
    // Salvar título original
    const originalTitle = document.title;
    
    // Log para debug
    console.log('[handlePrintClick] unitName recebido:', unitName);
    console.log('[handlePrintClick] tipo:', typeof unitName);
    console.log('[handlePrintClick] isEmpty:', !unitName || unitName.trim() === '');
    
    // Definir título para o PDF somente se unitName não estiver vazio
    if (unitName && unitName.trim() !== '') {
      const now = new Date();
      const data = now.toLocaleDateString('pt-BR').replaceAll('/', '-');
      const hora = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).replaceAll(':', '-');
      
      const newTitle = `${unitName.trim()}  ${data} ${hora}`;
      console.log('[handlePrintClick] Definindo título:', newTitle);
      document.title = newTitle;
      
      // Aguardar o título ser aplicado antes de imprimir
      setTimeout(() => {
        console.log('[handlePrintClick] Título atual antes de imprimir:', document.title);
        globalThis.print();
        
        // Usar beforeprint e afterprint para gerenciar o título
        const handleAfterPrint = () => {
          document.title = originalTitle;
          window.removeEventListener('afterprint', handleAfterPrint);
        };
        
        window.addEventListener('afterprint', handleAfterPrint);
        
        // Fallback caso afterprint não seja disparado
        setTimeout(() => {
          document.title = originalTitle;
          window.removeEventListener('afterprint', handleAfterPrint);
        }, 2000);
      }, 300);
    } else {
      console.warn('[handlePrintClick] Nome da unidade vazio ou inválido, imprimindo sem alterar título');
      globalThis.print();
    }
  }, []);
 
  const limparFormulario = useCallback(() => {
    resetForm();
    
    // Limpar seleções de propriedade
    propertySelection.handleEstadoChange('');
    propertySelection.handleCidadeChange('');
    propertySelection.handleEmpreendimentoChange('');
    propertySelection.handleBlocoChange('');
    propertySelection.handleUnidadeChange('');
    propertySelection.handlePagadoriaChange(false);
    propertySelection.handleRendaExteriorChange(false);
    
    resetSimulationState();
    showSuccess('Formulário limpo com sucesso!');
  }, [resetForm, propertySelection, resetSimulationState, showSuccess]);
 
  return {
    // State
    formData,
    planosDisponiveis,
    tiposFluxoDisponiveis,
    tiposCorrecaoDisponiveis,
    dadosInicializacao,
    valorFinal,
    valorTabelaState,
    loadingInicializacao,
    loadingSimulacao,
    simulationResult,
    showResult,
    isPrintEnabled,
    loadingUnidades: propertySelection.loadingUnidades,
    intermediates,

    // Property Selection - usando wrappers que ocultam o resultado
    loading: propertySelection.loading,
    estados: propertySelection.estados,
    cidades: propertySelection.cidades,
    empreendimentos: propertySelection.empreendimentos,
    blocos: propertySelection.blocos,
    unidades: propertySelection.unidades,
    selectedEstado: propertySelection.selectedEstado,
    selectedCidade: propertySelection.selectedCidade,
    selectedEmpreendimento: propertySelection.selectedEmpreendimento,
    selectedBloco: propertySelection.selectedBloco,
    selectedUnidade: propertySelection.selectedUnidade,
    valorTabela: propertySelection.valorTabela,
    isPagadoria: propertySelection.isPagadoria,
    isRendaExterior: propertySelection.isRendaExterior,
    selectedUnidadeData: propertySelection.selectedUnidadeData,
    selectedEmpreendimentoData: propertySelection.selectedEmpreendimentoData,
    handleEstadoChange,
    handleCidadeChange,
    handleEmpreendimentoChange,
    handleBlocoChange,
    handleUnidadeChange,
    handlePagadoriaChange,
    handleRendaExteriorChange,
    handleClearEmpreendimento,
    handleClearUnidade,
   
    // Actions
    updatePlano: updatePlanoWithHide, // Usar versão que oculta o resultado (centraliza limpeza em handlePlanoChange)
    limparFormulario,
    enviarAprovacao,
    handlePrintClick,
    setFormData: setFormDataWithHide, // Usar versão que oculta o resultado
    handleIntermediatesChange: handleIntermediatesChangeWithHide, // Usar versão que oculta o resultado
    handleFgtsChange, // Handler para FGTS que redistribui sinais
    handleDescontoChange, // Handler para Desconto que redistribui sinais
    
    // Signal Distribution Handlers - usando wrappers que ocultam o resultado
    handleAtoChange,
    handleCartaDeCreditoChange,
    handleE2Change,
    handleE3Change,
    handleQuantidadeParcelasChange,
    handleQuantidadeParcelasBlur,
    
    showError,
    showSuccess
  };
};